import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { MAIN_AREAS, COMPANY_INFO } from '../data/siteData';

export default function AreasHubPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('alle');
  const [selectedService, setSelectedService] = useState('alle');

  const filteredAreas = MAIN_AREAS.filter((area) => {
    const matchesSearch =
      area.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      area.region.toLowerCase().includes(searchQuery.toLowerCase()) ||
      area.localPages.some((lp) => lp.title.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesRegion =
      selectedRegion === 'alle' || area.region.toLowerCase() === selectedRegion.toLowerCase();

    return matchesSearch && matchesRegion;
  });

  return (
    <>
      <SEO
        title="Områder vi dækker - Certificeret Træpleje i Jylland og på Fyn"
        description="Oversigt over byer og områder dækket af Hærvejens Træpleje: Kolding, Fredericia, Vejle, Horsens, Skanderborg, Aarhus og hele Trekantområdet."
        canonical="https://xn--hrvejenstrpleje-xlbj.dk/omraader"
      />

      <Breadcrumbs items={[{ label: 'Områder vi dækker' }]} />

      {/* Hero */}
      <section className="bg-[#122b1f] text-white py-14 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs font-bold text-[#7dd2a6] bg-[#1a3d2c] px-3.5 py-1.5 rounded-full inline-block mb-4">
            Geografisk dækning
          </span>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
            Områder vi dækker
          </h1>
          <p className="text-base sm:text-lg text-[#cadcd1] leading-relaxed max-w-2xl mx-auto">
            Hærvejens Træpleje har base i Christiansfeld og servicerer private haver, virksomheder og forsyningsselskaber i hele Østjylland og Trekantområdet.
          </p>
        </div>
      </section>

      {/* Interactive Filter / Search Bar */}
      <section className="py-8 bg-[#f5f9f6] border-b border-[#dce8e0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-4">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
            {/* Search Input */}
            <div className="relative w-full lg:w-80">
              <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-[#65796d] text-sm" aria-hidden="true" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Søg by, f.eks. Kolding, Vejle..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#cfddd4] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#2d6a4f]/20 focus:border-[#2d6a4f]"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#809488] hover:text-[#183124]"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              )}
            </div>

            {/* Regional Filter Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto text-xs font-semibold">
              <span className="text-[#596d61] mr-1 shrink-0">Region:</span>
              {[
                { id: 'alle', label: 'Alle regioner' },
                { id: 'Trekantområdet', label: 'Trekantområdet' },
                { id: 'Østjylland', label: 'Østjylland' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setSelectedRegion(tab.id)}
                  className={`px-3.5 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap ${
                    selectedRegion === tab.id
                      ? 'bg-[#1b4332] text-white shadow-2xs font-bold'
                      : 'bg-white border border-[#cfddd4] text-[#34453a] hover:bg-[#eaf2ed]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Service Filter Tabs */}
            <div className="flex items-center gap-1.5 overflow-x-auto text-xs font-semibold">
              <span className="text-[#596d61] mr-1 shrink-0">Ydelse:</span>
              {[
                { id: 'alle', label: 'Alle ydelser' },
                { id: 'Træfældning', label: 'Træfældning' },
                { id: 'Topkapning', label: 'Topkapning' },
                { id: 'Rodfræsning', label: 'Rodfræsning' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setSelectedService(tab.id)}
                  className={`px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                    selectedService === tab.id
                      ? 'bg-[#296046] text-white shadow-2xs'
                      : 'bg-white border border-[#cfddd4] text-[#34453a] hover:bg-[#eaf2ed]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Areas Matrix */}
      <section className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredAreas.map((area) => {
            const visibleLocalPages =
              selectedService === 'alle'
                ? area.localPages
                : area.localPages.filter((lp) => lp.service === selectedService);

            return (
              <div
                key={area.slug}
                className="bg-white rounded-2xl overflow-hidden border border-[#dce5df] shadow-xs hover:border-[#357959] transition-all flex flex-col"
              >
                {/* Image Header */}
                <div className="relative aspect-16/9 overflow-hidden bg-[#e6ede7] flex items-center justify-center">
                  <img
                    src={area.image}
                    alt={`Træpleje i ${area.name}`}
                    className="w-full h-full object-contain p-2.5"
                    loading="lazy"
                  />
                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-md bg-[#163627]/90 text-white text-[11px] font-semibold backdrop-blur-xs">
                    {area.region}
                  </div>
                </div>

                <div className="p-6 flex-1 flex flex-col">
                  {/* City Title */}
                  <h2 className="text-2xl font-bold text-[#14291d] mb-1">
                    <Link to={`/${area.slug}`} className="hover:text-[#2d6a4f] transition-colors">
                      {area.name}
                    </Link>
                  </h2>
                  <p className="text-xs text-[#527a65] font-semibold mb-3">
                    {area.introHeadline}
                  </p>
                  <p className="text-sm text-[#485b50] leading-relaxed mb-5">
                    {area.metaDesc}
                  </p>

                  {/* Dedicated SEO Sub-pages list */}
                  <div className="mt-auto pt-4 border-t border-[#edf2ee] space-y-2">
                    <p className="text-xs font-bold text-[#355242]">
                      Landingssider i {area.name}:
                    </p>
                    <ul className="space-y-1.5">
                      {visibleLocalPages.map((lp) => (
                        <li key={lp.slug}>
                          <Link
                            to={`/${lp.slug}`}
                            className="flex items-center justify-between p-2 rounded-lg bg-[#f8faf8] hover:bg-[#e8f1ec] text-xs font-medium text-[#1c2c22] transition-colors group"
                          >
                            <span className="flex items-center gap-2">
                              <i className="fa-solid fa-arrow-turn-down-right text-[10px] text-[#2d6a4f]" aria-hidden="true" />
                              <span className="group-hover:text-[#1b4332] font-semibold">{lp.title}</span>
                            </span>
                            <i className="fa-solid fa-chevron-right text-[9px] text-[#8aa093]" aria-hidden="true" />
                          </Link>
                        </li>
                      ))}
                    </ul>

                    {/* Link to Main City Overview */}
                    <div className="pt-2">
                      <Link
                        to={`/${area.slug}`}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1b4332] hover:underline"
                      >
                        <span>Se komplet profil for {area.name} »</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredAreas.length === 0 && (
          <div className="text-center py-16 bg-[#f8f9f8] rounded-2xl border border-dashed border-[#ccd9d1]">
            <p className="text-base text-[#566b5f]">
              Ingen byer matcher den valgte filtrering.
            </p>
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setSelectedRegion('alle');
                setSelectedService('alle');
              }}
              className="mt-3 px-4 py-2 rounded-lg bg-[#1b4332] text-white text-xs font-semibold cursor-pointer"
            >
              Nulstil filtre
            </button>
          </div>
        )}
      </section>

      {/* Regional summary callout */}
      <section className="py-14 bg-[#f8f6f0] border-t border-[#e2ece6]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl p-8 border border-[#dce5df] shadow-xs">
            <h3 className="text-2xl font-bold text-[#14291d] mb-4">
              Dækker I også uden for de nævnte byer?
            </h3>
            <p className="text-base text-[#46594e] leading-relaxed mb-6">
              Ja, absolut! Selvom vi har dedikerede landingssider for de største byer i Trekantområdet og Østjylland (Kolding, Fredericia, Vejle, Horsens, Skanderborg og Aarhus), kører vi også dagligt ud til opland, landsbyer og sommerhusområder i hele regionen, herunder Christiansfeld, Haderslev, Middelfart og resten af det sydjyske og fynske område.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#1b4332] text-white font-semibold text-sm hover:bg-[#143627] whitespace-nowrap text-center"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring på {COMPANY_INFO.phone} for en besigtigelse</span>
              </a>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-[#2d6a4f] text-[#1b4332] font-semibold text-sm hover:bg-[#f2f7f4] text-center"
              >
                <span>Skriv til os</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
